import FolderOutlined from "@mui/icons-material/FolderOutlined";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { blue, blueGrey, grey } from "@mui/material/colors";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import { useMemo } from "react";

import { SubdirectoryArrowRightOutlined } from "@mui/icons-material";
import { useFileDocGroupAndTypesHook } from "../../hooks/useFileDocGroupAndTypesHook";
import Flx from "../layout/Flx";
import Txt from "../typography/Txt";

const SelectFileAssignmentInput = ({
  onChange,
  sx,
  docGroup,
  docType,
  size = "medium",
  placeholder,
  onBlur,
  error,
  helperText,

  // NEW: when true, the input only displays docType (instead of "docGroup > docType")
  onlyDisplayDocType = false,
}) => {
  const { getDocumentTypeOptions } = useFileDocGroupAndTypesHook();

  // Transform flat options into grouped structure
  const groupedOptions = useMemo(() => {
    const opt = getDocumentTypeOptions();

    return opt.map((option) => {
      const [group, type] = option.label.split(" > ");
      return {
        group,
        type,
        label: option.label,
        value: option.value,
      };
    });
  }, [getDocumentTypeOptions]);

  // Synthetic "{group} - Unassigned Type" option per docGroup. Picking
  // one sets docGroup to the chosen group and clears docType (so the
  // file lands in the group but has no specific type assigned). Sorted
  // first within each group so the catch-all sits above the typed
  // entries.
  const sortedOptions = useMemo(() => {
    const uniqueGroups = Array.from(
      new Set(groupedOptions.map((o) => o.group)),
    );
    const unassignedOptions = uniqueGroups.map((group) => ({
      group,
      type: "",
      label: `${group} - Unassigned Type`,
      value: `${group}::__unassigned__`,
      isUnassigned: true,
    }));
    return [...groupedOptions, ...unassignedOptions].sort((a, b) => {
      if (a.group !== b.group) return a.group.localeCompare(b.group);
      // Unassigned-type entry sits at the top of its group.
      if (a.isUnassigned && !b.isUnassigned) return -1;
      if (!a.isUnassigned && b.isUnassigned) return 1;
      return a.type.localeCompare(b.type);
    });
  }, [groupedOptions]);

  const selectedOption = useMemo(() => {
    if (!docGroup) return null;
    if (!docType) {
      // No specific type — match the synthetic "Unassigned Type" entry
      // for this group so the field stays in sync visually.
      return (
        sortedOptions?.find(
          (option) => option.group === docGroup && option.isUnassigned,
        ) || null
      );
    }
    return (
      sortedOptions?.find(
        (option) =>
          option.group === docGroup &&
          option.type === docType &&
          !option.isUnassigned,
      ) || null
    );
  }, [docGroup, docType, sortedOptions]);

  const displayValue = useMemo(() => {
    // If you only want to display docType, prefer docType when present.
    if (onlyDisplayDocType) {
      return docType || "";
    }

    if (docGroup && docType) return `${docGroup} > ${docType}`;
    // Surface the unassigned state explicitly in the input so it doesn't
    // look like the docType column is just blank for no reason.
    if (docGroup && !docType) return `${docGroup} > Unassigned Type`;

    return "";
  }, [docGroup, docType, onlyDisplayDocType]);

  const styles = useMemo(() => {
    return {
      ".MuiAutocomplete-endAdornment": {
        marginRight: "0px",
      },
      "& .MuiInputBase-input": {
        color: displayValue ? "inherit" : "rgba(0, 0, 0, 0.7)",
      },
      ...sx,
    };
  }, [sx, displayValue]);

  const emptyPlaceholderValue = useMemo(() => {
    if (placeholder) return placeholder;
    return "Select document type";
  }, [placeholder]);

  return (
    <Autocomplete
      loading={isNil(sortedOptions) || isEmpty(sortedOptions)}
      value={selectedOption}
      onBlur={onBlur}
      onChange={(event, newValue) => {
        if (!newValue) {
          onChange({ docGroup: null, docType: null });
          return;
        }

        onChange({
          docGroup: newValue.group,
          docType: newValue.type,
        });
      }}
      isOptionEqualToValue={(option, val) => {
        if (!option || !val) return false;
        return option.group === val.group && option.type === val.type;
      }}
      size={size}
      options={sortedOptions}
      groupBy={(option) => option.group}
      sx={styles}
      slotProps={{
        popper: {
          placement: "bottom-start",
          modifiers: [
            {
              name: "preventOverflow",
              options: { boundary: "viewport", padding: 8, altAxis: true },
            },
            { name: "flip", options: { boundary: "viewport", padding: 8 } },
          ],
          sx: {
            width: "min(520px, calc(100vw - 16px)) !important",
            maxWidth: "calc(100vw - 16px)",
            "& .MuiPaper-root": {
              width: "100%",
              maxWidth: "100%",
            },
            "& .MuiAutocomplete-listbox": {
              maxHeight: "500px",
            },
          },
        },
      }}
      getOptionLabel={(option) => {
        if (!option) return "";

        // This controls what is shown in the input for the selected option
        if (onlyDisplayDocType) return option.type || "";
        return option.label || "";
      }}
      renderGroup={(params) => (
        <li key={params.key}>
          <Box
            sx={{
              position: "sticky",
              top: "-8px",
              padding: "10px 16px",
              backgroundColor: blueGrey[50],
              borderBottom: `2px solid ${blueGrey[100]}`,
              fontWeight: 700,
              fontSize: "0.8125rem",
              color: blueGrey[800],
              textTransform: "uppercase",
              zIndex: 1,
              display: "flex",
              lineHeight: 1,
              alignItems: "center",
              gap: 1,
            }}
          >
            <FolderOutlined sx={{ fontSize: 18, color: blueGrey[600] }} />
            {params.group}
          </Box>
          <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
        </li>
      )}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        const isUnassigned = !!option.isUnassigned;

        return (
          <Box
            component="li"
            key={key}
            sx={{
              borderBottom: `1px solid ${grey[200]}`,
              padding: "10px 16px 10px 20px !important",
              // Subtle muted backdrop on the unassigned entry so it
              // reads as a catch-all rather than another type row.
              backgroundColor: isUnassigned ? blueGrey[50] : undefined,
              "&:hover": {
                backgroundColor: blue[50],
              },
              "&[aria-selected='true']": {
                backgroundColor: `${blue[100]} !important`,
                fontWeight: 600,
              },
            }}
            {...optionProps}
          >
            <Flx ac gap={1.5}>
              <SubdirectoryArrowRightOutlined
                className="thin"
                sx={{
                  fontSize: 16,
                  color: blueGrey[500],
                }}
              />
              <Txt
                sx={{
                  fontWeight: 400,
                  fontSize: "0.875rem",
                  fontStyle: isUnassigned ? "italic" : "normal",
                  color: isUnassigned ? blueGrey[600] : blueGrey[900],
                }}
              >
                {isUnassigned ? "Unassigned Type" : option.type}
              </Txt>
            </Flx>
          </Box>
        );
      }}
      renderInput={(params) => {
        const customParams = {
          ...params,
          inputProps: {
            ...params.inputProps,
            ...(selectedOption && displayValue ? { value: displayValue } : {}),
          },
        };

        return (
          <TextField
            {...customParams}
            placeholder={emptyPlaceholderValue}
            helperText={helperText}
            error={error}
          />
        );
      }}
    />
  );
};

export default SelectFileAssignmentInput;
